/**
 * <s-landing-reviews> — Swipeable scroll-snap review carousel with position dots and gentle auto-advance.
 *
 * Required child markup:
 *   [data-track]         — Horizontally scrolling list; each child is one slide.
 *   button[data-dot="n"] — Position dots; the current one has aria-current="true".
 *
 * Auto-advances every 5s (skipped with reduced motion) and stops for good once the shopper
 * swipes, taps, scrolls or focuses inside the carousel.
 */
class SLandingReviews extends HTMLElement {
  #track = null;
  #dots = [];
  #timer = 0;
  #frame = 0;
  #reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    this.#dots = [...this.querySelectorAll('[data-dot]')];
    if (!this.#track) return;

    this.#dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.#stop();
        this.goTo(Number(dot.dataset.dot));
      });
    });

    this.#track.addEventListener('scroll', () => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        this.#mark(this.#currentIndex());
      });
    }, { passive: true });

    ['pointerdown', 'touchstart', 'wheel', 'focusin', 'keydown'].forEach((type) => {
      this.#track.addEventListener(type, () => this.#stop(), { passive: true });
    });

    if (!this.#reduce && this.#slides().length > 1) {
      this.#timer = window.setInterval(() => {
        this.goTo((this.#currentIndex() + 1) % this.#slides().length);
      }, 5000);
    }
  }

  disconnectedCallback() {
    this.#stop();
  }

  goTo(index) {
    const slide = this.#slides()[index];
    if (!slide) return;
    const trackRect = this.#track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const offset = slideRect.left - trackRect.left - (trackRect.width - slideRect.width) / 2;
    this.#track.scrollBy({ left: offset, behavior: this.#reduce ? 'auto' : 'smooth' });
    this.#mark(index);
  }

  #slides() {
    return [...this.#track.children];
  }

  #currentIndex() {
    const trackRect = this.#track.getBoundingClientRect();
    const center = trackRect.left + trackRect.width / 2;
    let best = 0;
    let bestDistance = Infinity;
    this.#slides().forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    return best;
  }

  #mark(index) {
    this.#dots.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  #stop() {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = 0;
    }
  }
}

if (!customElements.get('s-landing-reviews')) {
  customElements.define('s-landing-reviews', SLandingReviews);
}
